import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import EditIcon from '@material-ui/icons/Edit';
import LinkIcon from '@material-ui/icons/Link';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useParams } from 'react-router';
import { urlRegex } from '../regex';
import openInNewTab from '../utils/openInNewTab';
import { updatePermit } from '../api/permits';

// Inline "link" field for Permit Details: shows the saved URL as a clickable
// button and edits it in a small popup that PATCHes the permit straight away.
// `valueKey` is both the key read off `initValues` and the key sent to the API,
// so the same component serves Permit Link and Approved Plans.
const PermitFormPermitLink = ({
  initValues,
  setLinkValue,
  name,
  label = 'Permit Link',
  valueKey = 'permitLink',
  allowClear = false,
  disabled = false,
}) => {
  const [show, setShow] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initValues?.[valueKey] || '');
  const [draft, setDraft] = useState<string>(initValues?.[valueKey] || '');
  const { permitId } = useParams();

  // `initValues` is empty on mount and filled once the permit request lands, so
  // without this the field renders blank — and the parent's save payload (which
  // reads the state pushed up by setLinkValue) would clear the stored link.
  useEffect(() => {
    const stored = initValues?.[valueKey] || '';
    setValue(stored);
    setLinkValue(stored);
  }, [initValues?.[valueKey]]);

  const openEditor = () => {
    if (disabled) return;
    setDraft(value);
    setError(false);
    setShow(true);
  };

  const onSave = async () => {
    const next = draft.trim();
    const isClearing = allowClear && next === '';
    if (!isClearing && !urlRegex.test(next)) {
      setError(true);
      return;
    }
    setValue(next);
    setLinkValue(next);
    await updatePermit({ [valueKey]: next, id: permitId });
    setShow(false);
  };

  const onOpen = () => {
    if (value) {
      openInNewTab(value);
    }
  };

  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Control>
        {value ? (
          <LinkButton type="button" onClick={onOpen} title={value}>
            <LinkIcon />
            <LinkText>{value}</LinkText>
            <OpenInNewIcon className="open" />
          </LinkButton>
        ) : (
          <Placeholder>No link added</Placeholder>
        )}
        {/* Opening the link stays available on a read-only permit — only the
            edit popup is gated, since it PATCHes the permit on Save. */}
        <EditButton
          type="button"
          onClick={openEditor}
          disabled={disabled}
          title={`Edit ${label.toLowerCase()}`}
        >
          <EditIcon />
        </EditButton>

        {show && (
          <Popup>
            <PopupLabel htmlFor={name}>{label}</PopupLabel>
            <PopupInput
              id={name}
              name={name}
              value={draft}
              placeholder="https://..."
              hasError={error}
              autoFocus
              onChange={e => {
                setDraft(e.target.value);
                if (error) setError(false);
              }}
            />
            {error && <Red>Link must start with http:// or https://</Red>}
            <Buttons>
              <GhostButton type="button" onClick={() => setShow(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton type="button" onClick={onSave}>
                Save
              </PrimaryButton>
            </Buttons>
          </Popup>
        )}
      </Control>
    </Field>
  );
};

export default PermitFormPermitLink;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 8px;
`;

const Control = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinkButton = styled.button`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--primary-700);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  svg {
    font-size: 18px;
    flex-shrink: 0;
    color: var(--neutral-500);
  }
  svg.open {
    margin-left: auto;
    font-size: 16px;
  }

  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const LinkText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Placeholder = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--neutral-400);
`;

const EditButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    color: var(--primary-600);
    background: var(--primary-50);
    border-color: var(--primary-600);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    background: var(--neutral-100);
    color: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const Popup = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 5;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
`;

const PopupLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 8px;
`;

const PopupInput = styled.input<{ hasError: boolean }>`
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${({ hasError }) => (hasError ? 'var(--danger-500)' : 'var(--border)')};
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }
  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Red = styled.div`
  margin-top: 6px;
  font-size: 0.75rem;
  color: var(--danger-500);
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const buttonBase = `
  min-width: 88px;
  height: 36px;
  padding: 0 16px;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
`;

const PrimaryButton = styled.button`
  ${buttonBase}
  border: none;
  background: var(--primary-600);
  color: var(--neutral-0);

  &:hover {
    background: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const GhostButton = styled.button`
  ${buttonBase}
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-700);

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
